// Platform Admin — Builder: ongoing structural editing of an existing draft definition. Creation
// of new definitions goes through WorkflowCreateWizardPage instead (this page is edit-only).
// Redesigned as a Stage → Process → Status tree (left) + detail panel (right) for whichever node
// is selected — Transitions/authority-per-transition now live on the Transitions map page, and
// Governance Gates/Automation Hooks on their own combined page (both linked from the top bar).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Modal } from '@/components/UI/Modal';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { Badge } from '@/components/UI/Badge';
import { Chip } from '@/components/UI/Chip';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import {
  useGetWorkflowDefinitionQuery,
  useUpdateWorkflowDefinitionMutation,
  usePublishWorkflowDefinitionMutation,
  CreateWorkflowRequest,
  StageDefinition,
  ProcessDefinition,
  StatusDefinition,
  EntryPointDefinition,
  TransitionDefinition,
  GovernanceGate,
  ApprovalType,
  AutomationHook,
  AuthorityRule,
} from '@/redux/internal-services/workflowApi';
import { useListCatalogueEntriesQuery } from '@/redux/internal-services/platformAdminApi';
import { validateWorkflowDraft } from './workflowValidation';

interface EngagementTypeOption { id: string; label: string }

// ─────────────────────────── Shared UI ────────────────────────────────────

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label className="block text-xs font-semibold text-ui-text2 mb-1">
    {children}{required && <span className="text-danger-text ml-1">*</span>}
  </label>
);
const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input {...props} className={`${fieldClass()} ${props.className ?? ''}`} />
);
const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select {...props} className={`${fieldClass()} ${props.className ?? ''}`} />
);
const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea {...props} className={`${fieldClass({ shape: 'box' })} resize-y min-h-20 ${props.className ?? ''}`} />
);

const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// ─────────────────────────── Draft shape ──────────────────────────────────

interface DraftState {
  definition_id: string;
  name: string;
  description: string;
  status: string;
  engagement_types: string[];
  stages: StageDefinition[];
  processes: ProcessDefinition[];
  statuses: StatusDefinition[];
  // Read-only passthrough here — edited on the Transitions map (Phase 4) and Governance &
  // Automation Hooks (Phase 5) pages, but round-tripped on every save so they aren't lost.
  entry_points: EntryPointDefinition[];
  transitions: TransitionDefinition[];
  governance_gates: GovernanceGate[];
  approval_types: ApprovalType[];
  automation_hooks: AutomationHook[];
  authority_rules: AuthorityRule[];
}

const emptyDraft = (): DraftState => ({
  definition_id: '', name: '', description: '', status: 'draft', engagement_types: [],
  stages: [], processes: [], statuses: [], entry_points: [],
  transitions: [], governance_gates: [], approval_types: [], automation_hooks: [], authority_rules: [],
});

type Selection =
  | { type: 'basic' }
  | { type: 'stage'; code: string }
  | { type: 'process'; code: string }
  | { type: 'status'; code: string };

// ─────────────────────────── Detail panels ────────────────────────────────

const BasicInfoPanel: React.FC<{ draft: DraftState; setDraft: React.Dispatch<React.SetStateAction<DraftState>> }> = ({ draft, setDraft }) => {
  const { data: catalogueData, isLoading: loadingTypes } = useListCatalogueEntriesQuery({ prefix: 'workflow_engine' });
  const engagementTypeOptions = useMemo<EngagementTypeOption[]>(() => {
    const entry = catalogueData?.data.find((c) => c.key === 'workflow_engine');
    const list = entry?.effective_value?.engagement_types as EngagementTypeOption[] | undefined;
    return list ?? [];
  }, [catalogueData]);

  const toggleType = (id: string) => {
    setDraft((d) => ({
      ...d,
      engagement_types: d.engagement_types.includes(id)
        ? d.engagement_types.filter((x) => x !== id)
        : [...d.engagement_types, id],
    }));
  };

  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <Label required>Workflow name</Label>
        <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} rows={3} />
      </div>
      <div>
        <Label>Engagement types</Label>
        {loadingTypes ? (
          <p className="text-xs text-ui-text3">Loading engagement types…</p>
        ) : engagementTypeOptions.length === 0 ? (
          <p className="text-xs text-ui-text3">No engagement types configured. Add them under the platform configuration catalogue (workflow_engine).</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {engagementTypeOptions.map((opt) => {
              const selected = draft.engagement_types.includes(opt.id);
              return (
                <Chip key={opt.id} selected={selected} onClick={() => toggleType(opt.id)}>
                  {opt.label}
                </Chip>
              );
            })}
          </div>
        )}
        <p className="text-xs text-ui-text3 mt-1">Engagement types this workflow applies to.</p>
      </div>
    </div>
  );
};

const StageDetailPanel: React.FC<{
  stage: StageDefinition; onSave: (s: StageDefinition) => void; onDelete: () => void;
}> = ({ stage, onSave, onDelete }) => {
  const [local, setLocal] = useState(stage);
  useEffect(() => setLocal(stage), [stage]);
  return (
    <div className="max-w-xl space-y-4">
      <div>
        <p className="text-xs text-ui-text3 font-mono mb-1">code: {stage.stage_code} · stable</p>
        <Label required>Display label</Label>
        <Input value={local.name} onChange={(e) => setLocal((s) => ({ ...s, name: e.target.value }))} />
      </div>
      <div>
        <Label required>Display order</Label>
        <Input type="number" value={local.display_order} onChange={(e) => setLocal((s) => ({ ...s, display_order: Number(e.target.value) }))} />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={local.description ?? ''} onChange={(e) => setLocal((s) => ({ ...s, description: e.target.value }))} />
      </div>
      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={local.tenant_label_renamable} onChange={(e) => setLocal((s) => ({ ...s, tenant_label_renamable: e.target.checked }))} />
        Tenant may rename this stage's label
      </label>
      <div className="flex justify-between pt-2">
        <Button variant="destructive-ghost" size="sm" onClick={onDelete}>Delete stage</Button>
        <Button size="sm" onClick={() => onSave(local)}>Save stage</Button>
      </div>
    </div>
  );
};

const ProcessDetailPanel: React.FC<{
  process: ProcessDefinition; stages: StageDefinition[]; onSave: (p: ProcessDefinition) => void; onDelete: () => void;
}> = ({ process, stages, onSave, onDelete }) => {
  const [local, setLocal] = useState(process);
  useEffect(() => setLocal(process), [process]);
  return (
    <div className="max-w-xl space-y-4">
      <div>
        <p className="text-xs text-ui-text3 font-mono mb-1">code: {process.process_code} · stable</p>
        <Label required>Display label</Label>
        <Input value={local.name} onChange={(e) => setLocal((p) => ({ ...p, name: e.target.value }))} />
      </div>
      <div>
        <Label required>Belongs to stage</Label>
        <Select value={local.stage_code} onChange={(e) => setLocal((p) => ({ ...p, stage_code: e.target.value }))}>
          {stages.map((s) => <option key={s.stage_code} value={s.stage_code}>{s.name}</option>)}
        </Select>
      </div>
      <div>
        <Label required>Display order</Label>
        <Input type="number" value={local.display_order} onChange={(e) => setLocal((p) => ({ ...p, display_order: Number(e.target.value) }))} />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={local.description ?? ''} onChange={(e) => setLocal((p) => ({ ...p, description: e.target.value }))} />
      </div>
      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={local.tenant_label_renamable} onChange={(e) => setLocal((p) => ({ ...p, tenant_label_renamable: e.target.checked }))} />
        Tenant may rename this process's label
      </label>
      <div className="flex justify-between pt-2">
        <Button variant="destructive-ghost" size="sm" onClick={onDelete}>Delete process</Button>
        <Button size="sm" onClick={() => onSave(local)}>Save process</Button>
      </div>
    </div>
  );
};

const StatusDetailPanel: React.FC<{
  status: StatusDefinition; processes: ProcessDefinition[]; onSave: (s: StatusDefinition) => void; onDelete: () => void;
}> = ({ status, processes, onSave, onDelete }) => {
  const [local, setLocal] = useState(status);
  useEffect(() => setLocal(status), [status]);
  const processName = processes.find((p) => p.process_code === local.process_code)?.name ?? local.process_code;

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <p className="text-xs text-ui-text3 font-mono mb-1">code: {status.status_code} · stable</p>
        <p className="text-xs text-ui-text3 mb-3">{processName} — changing the label never changes code, role, or type.</p>
        <Label required>Display label</Label>
        <Input value={local.name} onChange={(e) => setLocal((s) => ({ ...s, name: e.target.value }))} />
      </div>

      <div>
        <Label>Belongs to process</Label>
        <Input value={processName} disabled />
      </div>

      <div>
        <Label required>Operational type</Label>
        <Select value={local.operational_type} onChange={(e) => setLocal((s) => ({ ...s, operational_type: e.target.value as StatusDefinition['operational_type'] }))}>
          <option value="normal">Normal</option>
          <option value="on_hold">On Hold</option>
          <option value="closed">Closed</option>
          <option value="cancelled">Cancelled</option>
          <option value="rejected">Rejected</option>
        </Select>
      </div>

      <div>
        <Label>Entry / terminal designation</Label>
        <div className="space-y-1.5 mt-1">
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input
              type="checkbox"
              checked={local.lifecycle_role === 'entry'}
              onChange={(e) => setLocal((s) => ({ ...s, lifecycle_role: e.target.checked ? 'entry' : 'intermediate' }))}
            />
            Valid entry point
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input
              type="checkbox"
              checked={local.lifecycle_role === 'terminal'}
              onChange={(e) => setLocal((s) => ({ ...s, lifecycle_role: e.target.checked ? 'terminal' : 'intermediate' }))}
            />
            Terminal status
          </label>
        </div>
      </div>

      <div>
        <Label>Tenant permissions on this status</Label>
        <div className="space-y-1.5 mt-1">
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="checkbox" checked={local.tenant_label_renamable} onChange={(e) => setLocal((s) => ({ ...s, tenant_label_renamable: e.target.checked }))} />
            Tenant may rename display label
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="checkbox" checked={local.is_optional} onChange={(e) => setLocal((s) => ({ ...s, is_optional: e.target.checked }))} />
            Optional — tenant may activate / deactivate (off = always active)
          </label>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="destructive-ghost" size="sm" onClick={onDelete}>Delete status</Button>
        <Button size="sm" onClick={() => onSave(local)}>Save status</Button>
      </div>
    </div>
  );
};

// ─────────────────────────── Tree ─────────────────────────────────────────

const Tree: React.FC<{
  draft: DraftState;
  selection: Selection;
  onSelect: (s: Selection) => void;
  onAddStage: (name: string) => void;
  onAddProcess: (stageCode: string, name: string) => void;
  onAddStatus: (processCode: string, name: string) => void;
}> = ({ draft, selection, onSelect, onAddStage, onAddProcess, onAddStatus }) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [addingStage, setAddingStage] = useState(false);
  const [stageName, setStageName] = useState('');
  const [addingProcessFor, setAddingProcessFor] = useState<string | null>(null);
  const [processName, setProcessName] = useState('');
  const [addingStatusFor, setAddingStatusFor] = useState<string | null>(null);
  const [statusName, setStatusName] = useState('');

  const toggle = (code: string) => setCollapsed((c) => {
    const n = new Set(c);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });

  return (
    <div className="space-y-1">
      <button
        onClick={() => onSelect({ type: 'basic' })}
        className={`w-full text-left px-3 py-2 rounded-control text-sm mb-2 ${selection.type === 'basic' ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text2 hover:bg-ui-hover'}`}
      >
        Basic Info
      </button>

      {[...draft.stages].sort((a, b) => a.display_order - b.display_order).map((stage) => (
        <div key={stage.stage_code}>
          <div className="flex items-center gap-1">
            <button onClick={() => toggle(stage.stage_code)} className="text-ui-text3 text-xs w-4 shrink-0">{collapsed.has(stage.stage_code) ? '▸' : '▾'}</button>
            <button
              onClick={() => onSelect({ type: 'stage', code: stage.stage_code })}
              className={`flex-1 text-left px-2 py-1.5 rounded-control text-sm truncate ${selection.type === 'stage' && selection.code === stage.stage_code ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text hover:bg-ui-hover'}`}
            >
              {stage.name}
            </button>
          </div>

          {!collapsed.has(stage.stage_code) && (
            <div className="ml-5 pl-2 border-l border-ui-border2 space-y-0.5 mt-0.5">
              {draft.processes.filter((p) => p.stage_code === stage.stage_code).sort((a, b) => a.display_order - b.display_order).map((process) => (
                <div key={process.process_code}>
                  <button
                    onClick={() => onSelect({ type: 'process', code: process.process_code })}
                    className={`w-full text-left px-2 py-1 rounded-control text-xs truncate ${selection.type === 'process' && selection.code === process.process_code ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text2 hover:bg-ui-hover'}`}
                  >
                    {process.name}
                  </button>
                  <div className="ml-3 pl-2 border-l border-ui-border2 space-y-0.5">
                    {draft.statuses.filter((s) => s.process_code === process.process_code).sort((a, b) => a.display_order - b.display_order).map((status) => (
                      <button
                        key={status.status_code}
                        onClick={() => onSelect({ type: 'status', code: status.status_code })}
                        className={`w-full text-left px-2 py-1 rounded-control text-xs truncate flex items-center gap-1 ${selection.type === 'status' && selection.code === status.status_code ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text3 hover:bg-ui-hover'}`}
                      >
                        {status.lifecycle_role === 'entry' && <span className="text-accent-text">●</span>}
                        {status.lifecycle_role === 'terminal' && <span className="text-danger-text">●</span>}
                        <span className="truncate">{status.name}</span>
                      </button>
                    ))}
                    {addingStatusFor === process.process_code ? (
                      <div className="flex gap-1 mt-1">
                        <input autoFocus value={statusName} onChange={(e) => setStatusName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && statusName.trim()) { onAddStatus(process.process_code, statusName.trim()); setStatusName(''); setAddingStatusFor(null); } }}
                          className={`${fieldClass({ size: 'xs' })} flex-1 min-w-0`} placeholder="Status name" />
                        <IconButton size="xs" label="Add status" className="text-accent-text" onClick={() => { if (statusName.trim()) { onAddStatus(process.process_code, statusName.trim()); setStatusName(''); } setAddingStatusFor(null); }}>✓</IconButton>
                      </div>
                    ) : (
                      <Button variant="ghost" size="xs" onClick={() => setAddingStatusFor(process.process_code)}>+ Status</Button>
                    )}
                  </div>
                </div>
              ))}
              {addingProcessFor === stage.stage_code ? (
                <div className="flex gap-1 mt-1">
                  <input autoFocus value={processName} onChange={(e) => setProcessName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && processName.trim()) { onAddProcess(stage.stage_code, processName.trim()); setProcessName(''); setAddingProcessFor(null); } }}
                    className={`${fieldClass({ size: 'xs' })} flex-1 min-w-0`} placeholder="Process name" />
                  <IconButton size="xs" label="Add process" className="text-accent-text" onClick={() => { if (processName.trim()) { onAddProcess(stage.stage_code, processName.trim()); setProcessName(''); } setAddingProcessFor(null); }}>✓</IconButton>
                </div>
              ) : (
                <Button variant="ghost" size="xs" onClick={() => setAddingProcessFor(stage.stage_code)}>+ Add process</Button>
              )}
            </div>
          )}
        </div>
      ))}

      {addingStage ? (
        <div className="flex gap-1 mt-1">
          <input autoFocus value={stageName} onChange={(e) => setStageName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && stageName.trim()) { onAddStage(stageName.trim()); setStageName(''); setAddingStage(false); } }}
            className={`${fieldClass({ size: 'xs' })} flex-1 min-w-0`} placeholder="Stage name" />
          <IconButton size="xs" label="Add stage" className="text-accent-text" onClick={() => { if (stageName.trim()) { onAddStage(stageName.trim()); setStageName(''); } setAddingStage(false); }}>✓</IconButton>
        </div>
      ) : (
        <Button variant="outline" size="sm" className="mt-1 w-full" onClick={() => setAddingStage(true)}>+ Stage</Button>
      )}
    </div>
  );
};

// ─────────────────────────── Main Page ────────────────────────────────────

const WorkflowEditorPage: React.FC = () => {
  const { definitionId, version } = useParams<{ definitionId: string; version: string }>();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<DraftState>(emptyDraft());
  const [selection, setSelection] = useState<Selection>({ type: 'basic' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showValidate, setShowValidate] = useState(false);

  const { data, isLoading } = useGetWorkflowDefinitionQuery({ definition_id: definitionId!, version: Number(version) });
  const [updateDraft] = useUpdateWorkflowDefinitionMutation();
  const [publishDraft, { isLoading: publishing }] = usePublishWorkflowDefinitionMutation();

  useEffect(() => {
    if (!data?.data) return;
    const d = data.data;
    setDraft({
      definition_id: d.definition_id,
      name: d.name,
      description: d.description,
      status: d.status,
      engagement_types: d.engagement_types,
      stages: d.stages as StageDefinition[],
      processes: d.processes as ProcessDefinition[],
      statuses: d.statuses as StatusDefinition[],
      entry_points: d.entry_points as EntryPointDefinition[],
      transitions: d.transitions as TransitionDefinition[],
      governance_gates: d.governance_gates as GovernanceGate[],
      approval_types: (d.approval_types ?? []) as ApprovalType[],
      automation_hooks: d.automation_hooks as AutomationHook[],
      authority_rules: d.authority_rules as AuthorityRule[],
    });
  }, [data]);

  const addStage = (name: string) => setDraft((d) => ({ ...d, stages: [...d.stages, {
    stage_code: slug(name), name, display_order: d.stages.length + 1, description: '', is_custom: false, tenant_label_renamable: true,
  }] }));
  const addProcess = (stageCode: string, name: string) => setDraft((d) => ({ ...d, processes: [...d.processes, {
    process_code: slug(name), stage_code: stageCode, name, display_order: d.processes.filter((p) => p.stage_code === stageCode).length + 1, description: '', is_custom: false, tenant_label_renamable: true,
  }] }));
  const addStatus = (processCode: string, name: string) => setDraft((d) => ({ ...d, statuses: [...d.statuses, {
    status_code: slug(name), process_code: processCode, name, display_order: d.statuses.filter((s) => s.process_code === processCode).length + 1,
    lifecycle_role: 'intermediate', operational_type: 'normal', is_optional: false, is_custom: false, is_active: true, tenant_label_renamable: true, description: '',
  }] }));

  const saveStage = (updated: StageDefinition) => { setDraft((d) => ({ ...d, stages: d.stages.map((s) => s.stage_code === updated.stage_code ? updated : s) })); };
  const deleteStage = (code: string) => { setDraft((d) => ({ ...d, stages: d.stages.filter((s) => s.stage_code !== code) })); setSelection({ type: 'basic' }); };
  const saveProcess = (updated: ProcessDefinition) => { setDraft((d) => ({ ...d, processes: d.processes.map((p) => p.process_code === updated.process_code ? updated : p) })); };
  const deleteProcess = (code: string) => { setDraft((d) => ({ ...d, processes: d.processes.filter((p) => p.process_code !== code) })); setSelection({ type: 'basic' }); };
  const saveStatus = (updated: StatusDefinition) => { setDraft((d) => ({ ...d, statuses: d.statuses.map((s) => s.status_code === updated.status_code ? updated : s) })); };
  const deleteStatus = (code: string) => { setDraft((d) => ({ ...d, statuses: d.statuses.filter((s) => s.status_code !== code) })); setSelection({ type: 'basic' }); };

  const buildRequestBody = (): CreateWorkflowRequest => {
    // Entry points aren't edited directly here — derived from statuses whose lifecycle_role is
    // 'entry', applied to every engagement type this workflow covers. Existing per-status
    // descriptions are preserved if present.
    const entryPoints: EntryPointDefinition[] = draft.statuses
      .filter((s) => s.lifecycle_role === 'entry')
      .map((s) => ({
        status_code: s.status_code,
        engagement_types: draft.engagement_types,
        description: draft.entry_points.find((e) => e.status_code === s.status_code)?.description ?? '',
      }));
    return {
      name: draft.name,
      description: draft.description,
      engagement_types: draft.engagement_types,
      stages: draft.stages,
      processes: draft.processes,
      statuses: draft.statuses,
      entry_points: entryPoints,
      transitions: draft.transitions,
      governance_gates: draft.governance_gates,
      approval_types: draft.approval_types,
      automation_hooks: draft.automation_hooks,
      authority_rules: draft.authority_rules,
    };
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await updateDraft({ definition_id: definitionId!, version: Number(version), body: buildRequestBody() }).unwrap();
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setError(err?.data?.message ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setError('');
    try {
      await updateDraft({ definition_id: definitionId!, version: Number(version), body: buildRequestBody() }).unwrap();
      await publishDraft({ definition_id: definitionId!, version: Number(version) }).unwrap();
      navigate(`/platform/workflows/${definitionId}/${version}`);
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setError(err?.data?.message ?? 'Failed to publish.');
    }
  };

  const validationChecks = validateWorkflowDraft({
    statuses: draft.statuses, transitions: draft.transitions, entryPoints: draft.entry_points, automationHooks: draft.automation_hooks,
  });

  if (isLoading) {
    return <PageShell header={null}><LoadingState label="Loading…" className="py-20" /></PageShell>;
  }

  const selectedStage = selection.type === 'stage' ? draft.stages.find((s) => s.stage_code === selection.code) : undefined;
  const selectedProcess = selection.type === 'process' ? draft.processes.find((p) => p.process_code === selection.code) : undefined;
  const selectedStatus = selection.type === 'status' ? draft.statuses.find((s) => s.status_code === selection.code) : undefined;

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          back={<Button variant="link" size="xs" onClick={() => navigate(-1)}>← Back</Button>}
          title={draft.name || 'Workflow'}
          subtitle={
            <>
              <span className="block">Workflow Definitions / {draft.name}</span>
              <Badge variant="neutral" className="mt-1 uppercase">v{version} · {draft.status}</Badge>
            </>
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {error && <p className="max-w-xs text-xs text-danger-text">{error}</p>}
              <Button variant="outline" size="sm" onClick={() => navigate(`/platform/workflows/${definitionId}/${version}/flowchart`)}>Transitions</Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/platform/workflows/${definitionId}/${version}/governance`)}>Governance & Hooks</Button>
              <Button variant="outline" size="sm" onClick={() => setShowValidate(true)}>Validate</Button>
              <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
              {draft.status === 'draft' && (
                <Button size="sm" onClick={handlePublish} disabled={publishing}>{publishing ? 'Publishing…' : 'Publish revision →'}</Button>
              )}
            </div>
          }
        />
      }
    >
      <div className="flex gap-6">
        <div className="w-64 shrink-0">
          <p className="eyebrow mb-2">Stage → Process → Status</p>
          <Tree draft={draft} selection={selection} onSelect={setSelection} onAddStage={addStage} onAddProcess={addProcess} onAddStatus={addStatus} />
        </div>

        <div className="card-surface min-w-0 flex-1 p-6">
          {selection.type === 'basic' && <BasicInfoPanel draft={draft} setDraft={setDraft} />}
          {selection.type === 'stage' && selectedStage && <StageDetailPanel stage={selectedStage} onSave={saveStage} onDelete={() => deleteStage(selectedStage.stage_code)} />}
          {selection.type === 'process' && selectedProcess && <ProcessDetailPanel process={selectedProcess} stages={draft.stages} onSave={saveProcess} onDelete={() => deleteProcess(selectedProcess.process_code)} />}
          {selection.type === 'status' && selectedStatus && <StatusDetailPanel status={selectedStatus} processes={draft.processes} onSave={saveStatus} onDelete={() => deleteStatus(selectedStatus.status_code)} />}
        </div>
      </div>

      {showValidate && (
        <Modal title="Validate workflow" onClose={() => setShowValidate(false)}>
          <div className="space-y-1.5">
            {validationChecks.map((c) => (
              <div key={c.id} className="flex items-start gap-2 text-xs">
                <span className={c.severity === 'pass' ? 'text-success' : 'text-warning-text'}>{c.severity === 'pass' ? '✓' : '▲'}</span>
                <div>
                  <p className="text-ui-text">{c.label}</p>
                  {c.detail && <p className="text-ui-text3">{c.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </PageShell>
  );
};

export default WorkflowEditorPage;
